import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams, useNavigate, Navigate } from 'react-router-dom';
import PatientPortalLayout from './components/portal/PatientPortalLayout';
import ProfileSection from './components/portal/ProfileSection';
import ClinicalChartSection from './components/portal/ClinicalChartSection';
import FormTrackerSection from './components/portal/FormTrackerSection';
import OnboardingWizard from './components/portal/OnboardingWizard';
import PatientFormViewer from './components/PatientFormViewer';
import SurveyEngine from './shared/ui/survey/SurveyEngine';
import VoiceInput from './components/VoiceInput';
import PatientLogin from './components/portal/PatientLogin';
import BridgeLanding from './components/portal/BridgeLanding';
import BridgeOnboardingPage from './components/portal/BridgeOnboardingPage';
import BridgeStatusPage from './components/portal/BridgeStatusPage';
import BridgeRenewPage from './components/portal/BridgeRenewPage';
import BridgePrescriberPage from './components/portal/BridgePrescriberPage';
import PinSetupPage from './components/portal/PinSetupPage';
import AccessRequestPage from './components/portal/AccessRequestPage';
import CoverageInquiryPage from './components/portal/CoverageInquiryPage';
import { supabase, isSupabaseConfigured } from './lib/supabaseClient';
import { isBridgeMode } from './lib/bridgeClient';
import type { PortalPatientProfile } from './types/shared';

interface PatientSession {
  token?: string;
  email?: string;
  [key: string]: unknown;
}

const PROFILE_KEY = "parseo_patient_profile";
const SESSION_KEY = "parseo_patient_session";

// One-time migration from the legacy (Parseo Health) localStorage keys so
// returning patients keep their profile + session through the rebrand.
const LEGACY_KEYS = {
  parseo_patient_profile: "huggable_patient_profile",
  parseo_patient_session: "huggable_patient_session",
};
(function migrateLegacyKeys() {
  try {
    for (const [newKey, oldKey] of Object.entries(LEGACY_KEYS)) {
      if (localStorage.getItem(newKey) == null) {
        const legacy = localStorage.getItem(oldKey);
        if (legacy != null) localStorage.setItem(newKey, legacy);
      }
      localStorage.removeItem(oldKey);
    }
  } catch { /* localStorage unavailable — ignore */ }
})();

function loadProfile(): PortalPatientProfile | null {
  try {
    const stored = localStorage.getItem(PROFILE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch { return null; }
}

function saveProfile(profile: PortalPatientProfile) {
  localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
}

// --- Route Components ---

interface DashboardPageProps {
  profile: PortalPatientProfile | null;
  setProfile: React.Dispatch<React.SetStateAction<PortalPatientProfile | null>>;
  session: PatientSession | null;
  onLogout: () => void;
}

function DashboardPage({ profile, setProfile, session, onLogout }: DashboardPageProps) {
  const navigate = useNavigate();
  const patientId = (profile?.id || profile?.patientId || session?.email) as string | undefined;
  const [activeSection, setActiveSection] = useState("forms");

  const isSetUp = !!(profile && profile.firstName);

  // Signed in but no profile yet — give a clear way forward instead of a
  // dead end. (Only reached with an active session; anonymous visitors get
  // the sign-in screen.)
  if (!isSetUp) {
    return (
      <PatientPortalLayout patientId={patientId} onLogout={onLogout}>
        <div style={{ textAlign: "center", padding: "60px 20px" }}>
          <div style={{
            width: 80, height: 80, borderRadius: "50%", backgroundColor: "#e4ecee",
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 24px", fontSize: 36,
          }}>
            &#128075;
          </div>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: "#182a33", marginBottom: 8 }}>
            Welcome to Parseo Health
          </h2>
          <p style={{ fontSize: 16, color: "#5b6e7c", lineHeight: 1.5, maxWidth: 480, margin: "0 auto 28px" }}>
            Let's set up your profile. It only takes a minute, and you'll do it
            once — after that, the forms your provider sends arrive pre-filled
            and ready for you to review and sign.
          </p>
          <button
            onClick={() => navigate("/profile")}
            style={{
              padding: "12px 28px", fontSize: 16, fontWeight: 600, borderRadius: 8,
              border: "none", cursor: "pointer", backgroundColor: "#182a33", color: "white",
            }}
          >
            Set up your profile
          </button>
        </div>
      </PatientPortalLayout>
    );
  }

  return (
    <PatientPortalLayout
      patientId={patientId}
      profile={profile}
      activeSection={activeSection}
      onSectionChange={setActiveSection}
      onLogout={onLogout}
    >
      {activeSection === "profile" && (
        <ProfileSection
          profile={profile}
          onProfileSaved={(updated) => setProfile(updated)}
        />
      )}
      {activeSection === "chart" && (
        <ClinicalChartSection patientId={patientId} />
      )}
      {activeSection === "forms" && (
        <FormTrackerSection patientId={patientId} />
      )}
    </PatientPortalLayout>
  );
}

// OnboardingPage removed — self-serve onboarding is no longer supported.
// All patients onboard via a physician-dispatched magic link, which lands
// on BridgeOnboardingPage and runs OnboardingWizard inside the bridge flow.

interface ProfilePageProps {
  profile: PortalPatientProfile | null;
  setProfile: React.Dispatch<React.SetStateAction<PortalPatientProfile | null>>;
}

function ProfilePage({ profile, setProfile }: ProfilePageProps) {
  const navigate = useNavigate();
  const patientId = ((profile as any)?.id || profile?.patientId) as string | undefined;
  return (
    <PatientPortalLayout patientId={patientId}>
      <OnboardingWizard
        initialProfile={profile as any}
        onComplete={(data) => {
          const profileData = (data.profile || data) as PortalPatientProfile;
          saveProfile(profileData);
          setProfile(profileData);
          navigate("/dashboard");
        }}
      />
    </PatientPortalLayout>
  );
}

function FormViewerPage() {
  return <PatientFormViewer />;
}

function SurveyPage() {
  const { formId } = useParams();
  const navigate = useNavigate();

  return (
    <PatientPortalLayout>
      <SurveyEngine
        formId={formId}
        mode="patient"
        VoiceInput={VoiceInput}
        onComplete={() => navigate("/dashboard")}
        onBack={() => navigate("/dashboard")}
      />
    </PatientPortalLayout>
  );
}

// --- App ---

function App() {
  const [profile, setProfile] = useState<PortalPatientProfile | null>(loadProfile);
  const [session, setSession] = useState<PatientSession | null>(null);
  const [authLoading, setAuthLoading] = useState(isSupabaseConfigured());

  // Check Supabase session on mount
  useEffect(() => {
    // Restore any persisted session first. This covers the demo / REST auth
    // path (no Supabase configured), so a signed-in patient stays signed in
    // across reloads instead of bouncing back to the login screen.
    const stored = localStorage.getItem(SESSION_KEY);
    if (stored) {
      try {
        setSession(JSON.parse(stored));
      } catch {}
    }

    if (!isSupabaseConfigured()) {
      setAuthLoading(false);
      return;
    }

    // Listen for auth state changes
    if (supabase) {
      supabase.auth.getSession().then(({ data: { session: s } }: any) => {
        if (s) {
          setSession(s);
          localStorage.setItem(SESSION_KEY, JSON.stringify(s));
        }
        setAuthLoading(false);
      });

      const { data: { subscription } } = supabase.auth.onAuthStateChange((_event: any, s: any) => {
        setSession(s);
        if (s) {
          localStorage.setItem(SESSION_KEY, JSON.stringify(s));
        } else {
          localStorage.removeItem(SESSION_KEY);
        }
      });

      return () => subscription?.unsubscribe();
    }
    setAuthLoading(false);
  }, []);

  function handleLogin({ token, email }: PatientSession) {
    setSession({ token, email });
    localStorage.setItem(SESSION_KEY, JSON.stringify({ token, email }));
    // Load profile from backend if possible
    if (email) {
      fetch(`/api/patient/profile/${encodeURIComponent(email)}`)
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data?.profile) {
            saveProfile(data.profile);
            setProfile(data.profile);
          }
        })
        .catch(() => {});
    }
  }

  function handleLogout() {
    if (supabase) supabase.auth.signOut().catch(() => {});
    setSession(null);
    setProfile(null);
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(PROFILE_KEY);
  }

  if (authLoading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f5f0eb" }}>
        <p style={{ color: "#5b6e7c" }}>Loading...</p>
      </div>
    );
  }

  // Bridge mode bypasses the Supabase gate entirely. We detect it either
  // by URL (magic link landing or onboarding paths) or by the presence
  // of an existing bridge session in sessionStorage.
  // `/signer/` counts as a bridge path even though it carries no bridge
  // session: the PIN-setup link is opened by a physician who has no account
  // here and never will, so the patient sign-in screen must not intercept it.
  // `/request/` likewise: a patient asking a clinic for a form from the
  // clinic's website has no account and no session (2026-10-23).
  const bridgePath = typeof window !== "undefined" && (
    window.location.pathname.startsWith("/r/") ||
    window.location.pathname.startsWith("/bridge/") ||
    window.location.pathname.startsWith("/signer/") ||
    window.location.pathname.startsWith("/request/")
  );
  const inBridge = bridgePath || isBridgeMode();

  // No active session and not on a magic-link (bridge) route → show sign-in.
  // Auth runs through the backend /api/auth/patient/* endpoints (demo mode
  // locally, Supabase in prod), so this no longer hinges on a Supabase URL
  // being configured — the portal is usable in plain local dev.
  if (!session && !inBridge) {
    return <PatientLogin onLogin={handleLogin} />;
  }

  return (
    <Router>
      <Routes>
        <Route path="/r/:token" element={<BridgeLanding />} />
        <Route path="/bridge/onboard" element={<BridgeOnboardingPage />} />
        <Route path="/bridge/status" element={<BridgeStatusPage />} />
        <Route path="/bridge/renew" element={<BridgeRenewPage />} />
        <Route path="/bridge/coverage" element={<CoverageInquiryPage />} />
        {/* Model A prescriber leg: review-and-sign, and the one-time PIN setup
            the physician does at onboarding (no session — the token is the
            credential). */}
        <Route path="/bridge/prescriber" element={<BridgePrescriberPage />} />
        <Route path="/signer/pin-setup" element={<PinSetupPage />} />
        {/* A patient asks a clinic for a form from the clinic's website
            (2026-10-23). Public: the page seals what it collects. */}
        <Route path="/request/:slug/:formKey" element={<AccessRequestPage />} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardPage profile={profile} setProfile={setProfile} session={session} onLogout={handleLogout} />} />
        {/* /onboarding intentionally removed — see invite-only model. */}
        <Route path="/profile" element={<ProfilePage profile={profile} setProfile={setProfile} />} />
        <Route path="/form/:formId" element={<FormViewerPage />} />
        <Route path="/survey/:formId" element={<FormViewerPage />} />
      </Routes>
    </Router>
  );
}

export default App;
